import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import ValidationElement, { newGuid } from 'components/Form/ValidationElement';

import {
  getSafeDateTime, withinOneMonth, getApplicantRequiredDuration, getRequiredRange, findTimelineGaps, today, allDatesValid
} from 'helpers/date';

import { DateTime, Interval } from 'luxon';
import { decimalAdjust } from '../dateranges';

export default class SummaryProgress extends ValidationElement {
  total() {
    return parseInt(this.props.total, 10);
  }

  /**
   * Compile the ranges from the list of items
   */
  ranges() {
    const {
      List, total, applicantBirthdate, thresholdYears, className, formType
    } = this.props;
    let items = [];
    if (List) {
      items = [...(List() || [])];
    } else {
      console.warn('No List() function was provided for Summary Progress');
    }

    const { coverage } = getApplicantRequiredDuration({
      requiredDuration: { years: total },
      applicantBirthdate,
      thresholdYears,
      type: className,
      formType
    });

    const requiredRange = getRequiredRange(coverage);

    const targetStart = DateTime.fromJSDate(requiredRange.fromExact.toJSDate());
    const targetInterval = Interval.fromDateTimes(targetStart, today);

    // const luxonStartDateSort = (a, b) => a < b ? -1 : a > b ? 1 : 0; //possible becuase Luxon DateTime implements valueOf
    const result = [];
    // Then loop through the items
    for (const item of items) {
      let left = 0;
      let width = 0;
      const dates = {};
      const from = getSafeDateTime(item?.from);
      dates.from = from.valid ? from.datetime : null;
      const to = getSafeDateTime(item?.to);
      const defaultTo = to.valid ? to.datetime : null;
      dates.to = (item?.present === true ? today : defaultTo);
      if (dates.from && dates.to) {
        const dateFrom = withinOneMonth(targetStart, dates.from) ? dates.from : dates.from.minus({ months: 1 });
        const currentInterval = Interval.fromDateTimes(dateFrom, dates.to);
        const preceedingInterval = Interval.fromDateTimes(targetStart, dateFrom);
        const preceedingIntervalDays = preceedingInterval.toDuration('days').days || 0;
        const totalDays = targetInterval.toDuration('days').days;
        const consideredInterval = currentInterval.intersection(targetInterval);
        const consideredIntervalDays = consideredInterval?.toDuration('days').days || 0;

        if (preceedingIntervalDays > 0 && totalDays > 0) {
          const percentageStart = preceedingIntervalDays / totalDays;
          left = (percentageStart * 100);
        }
        if (consideredIntervalDays > 0 && totalDays > 0) {
          const percentCovered = consideredIntervalDays / totalDays;
          width = (percentCovered * 100);
        }
        result.push({
          left,
          width: decimalAdjust('round', width, -2),
          dates: {
            from: dateFrom.startOf('month'),
            to: dates.to.endOf('month')
          },
        });
      }
    }
    return result;
  }

  /**
   * Calculate the amount of units based on the sum of percentages
   * in compiled ranges.
   */
  completed() {
    const {
      List, total, applicantBirthdate, thresholdYears, className, formType
    } = this.props;
    const { coverage, requiredYears } = getApplicantRequiredDuration({
      requiredDuration: {
        years: total
      },
      applicantBirthdate,
      thresholdYears,
      type: className,
      formType
    });
    const dates = List() || [];
    const gaps = findTimelineGaps(coverage, dates);

    // If not gaps exist, then match requiredYears; occurs in scenario where applicant is not required to document max duration for field due to age.
    if (dates.length > 0 && gaps.length === 0 && allDatesValid(dates)) {
      return requiredYears;
    }

    const requiredRange = getRequiredRange(coverage);
    const targetStart = DateTime.fromJSDate(requiredRange.fromExact.toJSDate());
    const targetInterval = Interval.fromDateTimes(targetStart, today);
    const localIntervals = this.ranges().map((x) => {
      const startDt = getSafeDateTime(x.dates.from)?.datetime || today;
      let endDt = getSafeDateTime(x.dates.to)?.datetime || today;
      endDt = endDt.plus({ months: 1 }).minus({ days: endDt.day + 0.01 }); // Fixing some weird julien end date with this.ranges being mid-month
      return Interval.fromDateTimes(startDt, endDt);
    });
    if (localIntervals.length === 0) {
      return 0;
    }
    // Combine all the ranges into the minimum number of ranges
    const mergedRanges = Interval.merge(localIntervals);
    // calculate how much time is in each interval compared with the desired range by months
    const reducedranges = mergedRanges.map((x) => x.intersection(targetInterval));
    // Get all the lengths as months and add them together.
    let months = 0;
    for (const interval of reducedranges) {
      months += interval?.toDuration('months').months || 0;
    }
    // Return the total number of months divided by 12
    return (Math.floor(months / 12));
  }

  /**
   * Provide visualized timeline from compiled ranges
   */
  timeline() {
    // Loop through the collected ranges and output as elements
    //
    // Example: 10 years ago |----- Gap -----|----- Filled -----|----- Gap -----| Today
    // Imagine 10 years ago is 0% and today is 100%
    return this.ranges().map((range) => {
      const styles = {
        left: `${range.left}%`,
        width: `${Math.abs(range.width)}%`,
      };

      return (
        <div
          key={newGuid()}
          className="filled"
          style={styles}
          data-from={getSafeDateTime(range.dates.from)}
          data-to={getSafeDateTime(range.dates.to)}
        />
      );
    });
  }

  render() {
    const {
      className, applicantBirthdate, title, unit, thresholdYears, formType
    } = this.props;
    const { requiredYears: total } = getApplicantRequiredDuration({
      requiredDuration: {
        years: this.props.total
      },
      applicantBirthdate,
      thresholdYears,
      type: className,
      formType
    });
    const completed = this.completed();

    const classes = classnames('summary-progress', className);

    const fractionClasses = classnames('fraction', { covered: completed === total });

    return (
      <div className={classes}>
        <div className="content">
          <div>
            {this.props.children}
            <span className="title">{title}</span>
          </div>
          <div className="progress">{this.timeline()}</div>
        </div>
        <div className="stats">
          <div className={fractionClasses}>
            <span className="completed">{completed}</span>
            <span className="slash" aria-label="of">/</span>
            <span className="total">{total}</span>
          </div>
          <span className="unit">{unit}</span>
        </div>
      </div>
    );
  }
}

SummaryProgress.propTypes = {
  total: PropTypes.number,
  List: PropTypes.func,
  className: PropTypes.string,
  children: PropTypes.node,
  title: PropTypes.node,
  unit: PropTypes.node,
  applicantBirthdate: PropTypes.object,
};

SummaryProgress.defaultProps = {
  total: 10,
  List: undefined,
  className: undefined,
  children: undefined,
  title: undefined,
  unit: undefined,
};
